import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { object, string } from "zod";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { changeUserPassword, resteUserPassword } from "../slices/changePasswordSlice";
import { useCallback } from "react";
import { openSnackbar } from "../../../redux/slices/snackbarSlice";

interface ChangePasswordInput {
  username: string;
  newPassword: string;
  confirmPassword: string;
}

const useValidationSchema = () => {
  const { t } = useTranslation();
  return object({
    username: string().min(1, t("login.validation.usernameRequired")),
    newPassword: string().min(
      1,
      t("changePassword.validation.passwordRequired")
    ),
    confirmPassword: string().min(
      1,
      t("changePassword.validation.passwordConfirmationRequired")
    ),
  });
};

const ChangePasswordForm = () => {
  const validationSchema = useValidationSchema();

  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const direction = useAppSelector((state) => state.languageSlice.direction);
  const { loading } = useAppSelector((state) => state.changePasswordSlice);
  const {roles} = useAppSelector((state) => state.authSlice);
  const name = useAppSelector((state) => state.authSlice.name);
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ChangePasswordInput>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      username: name || "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const onSubmit: SubmitHandler<ChangePasswordInput> = (data) => {
    if (roles.includes('Admin')) {
  
      dispatch(resteUserPassword(data)).then((result) => {
        if (resteUserPassword.fulfilled.match(result)) {
          dispatch(
            openSnackbar({
              message: "Mot de passe actualisé avec succès",
              type: "success",
            })
          );
          handleReset();
        } else if (resteUserPassword.rejected.match(result)) {
          dispatch(
            openSnackbar({
              message: (result.payload as string) || "Échec de la réinitialisation du mot de passe!",
              type: "error",
            })
          );
        }
      });
    } else if (roles.includes('Representant')) {
      dispatch(changeUserPassword(data)).then((result) => {
        if (changeUserPassword.fulfilled.match(result)) {
          dispatch(
            openSnackbar({
              message: "Mot de passe actualisé avec succès",
              type: "success",
            })
          );
          handleReset();
        } else if (changeUserPassword.rejected.match(result)) {
          dispatch(
            openSnackbar({
              message: (result.payload as string) || "Échec de la modification du mot de passe!",
              type: "error",
            })
          );
        }
      });
    }
  };
  

  const handleReset = useCallback(() => {
    reset();
  }, [reset]);


  return (
    <Box
      m={2}
      p={4}
      borderRadius={5}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        alignSelf: "center",
        maxWidth: {
          xs: "90%",
          sm: "90%",
          md: "90%",
          lg: "80%",
          xl: "80%",
        },
        border: "1px solid #E4E4EA",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
        backgroundColor: "#fff",
      }}
      dir={direction}
    >
      <Typography sx={{ color: "#182250" }} variant="h4" component="h3">
        {t("changePassword.title")}
      </Typography>
      <Typography sx={{ color: "#000" }} variant="caption">
        {t("changePassword.subTitile")}
      </Typography>

      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="username"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              margin="normal"
              fullWidth
              dir={direction}
              label={t("changePassword.email")}
              error={!!errors.username}
              helperText={errors.username ? errors.username.message : ""}
              InputProps={{ readOnly: roles.includes('Admin') ? false : true }}
            />
          )}
        />
        <Controller
          name="newPassword"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              margin="normal"
              fullWidth
              dir={direction}
              label={t("changePassword.newPassword")}
              type="password"
              error={!!errors.newPassword}
              helperText={errors.newPassword ? errors.newPassword.message : ""}
            />
          )}
        />
        <Controller
          name="confirmPassword"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              sx={{ marginBottom: "4%" }}
              margin="normal"
              fullWidth
              dir={direction}
              label={t("changePassword.confirmPassword")}
              type="password"
              error={!!errors.confirmPassword}
              helperText={
                errors.confirmPassword ? errors.confirmPassword.message : ""
              }
            />
          )}
        />
        <Button type="submit" fullWidth variant="contained" disabled={loading}>
          {loading ? (
            <CircularProgress size={24} />
          ) : (
            t("changePassword.submit")
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default ChangePasswordForm;
